import { useIntl } from '@leagueplatform/locales';
import { ParagraphText, TextAction } from '@leagueplatform/genesis-core';
import { StepScreen } from 'components/step-screen/step-screen.component';
import { useExternalLinks } from 'hooks/use-external-links/use-external-links.hook';
import {
  CONSENT_FLAGS,
  STEP_STRING_IDS,
} from 'components/onboarding-account-setup/onboarding-account-setup.constants';
import { useStepNavigation } from 'components/stepper/stepper.component';
import { usePatientPortalContext } from 'contexts/patient-portal-provider.context';
import { useOnboardingError } from 'components/onboarding-account-setup/contexts/onboarding-error.context';
import { useSetUserProfile } from 'hooks/mutations/use-set-user-profile.hook';
import { PATIENT_PORTAL_SYSTEM_IDS } from 'types/patient-portal-system.types';

const TermsStepLinkWrapper = (chunks: React.ReactNode, href: string) => (
  <TextAction
    as="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    css={{ typography: '$bodyTwo', fontWeight: '$headingOne' }}
  >
    {chunks}
  </TextAction>
);

const TermsStepLineBreak = () => <br />;

export const TermsStep = () => {
  const { formatMessage } = useIntl();
  const { goToNextStep } = useStepNavigation();
  const { systemId } = usePatientPortalContext();
  const externalLinks = useExternalLinks();

  const setUserProfileMutation = useSetUserProfile();
  const { reportError } = useOnboardingError();

  const isMyHealth = systemId === PATIENT_PORTAL_SYSTEM_IDS.MY_HEALTH;

  const handleTermsOnClick = () => {
    if (isMyHealth) {
      // If the user is completing account setup flow for myHealth skip updating consent until the final step of the flow
      goToNextStep();
      return;
    }

    // Only call mutation for this step when in Amplar
    setUserProfileMutation
      .mutateAsync({
        body: {
          custom_fields: [{ name: CONSENT_FLAGS.amplar, value: 'true' }],
        },
      })
      .then(() => {
        goToNextStep();
      })
      .catch((err: Error) => {
        reportError(err);
      });
  };

  return (
    <StepScreen
      heading={formatMessage({ id: STEP_STRING_IDS.terms.heading })}
      primaryButton={{
        label: formatMessage({ id: STEP_STRING_IDS.terms.primaryButtonLabel }),
        onClick: handleTermsOnClick,
        isButtonLoading: setUserProfileMutation.isPending,
      }}
    >
      <ParagraphText size="sm">
        {formatMessage(
          { id: STEP_STRING_IDS.terms.body },
          {
            privacyLink: (k: React.ReactNode) =>
              TermsStepLinkWrapper(k, externalLinks.PRIVACY),
            termsLink: (v: React.ReactNode) =>
              TermsStepLinkWrapper(v, externalLinks.TERMS),
            br: TermsStepLineBreak,
          },
        )}
      </ParagraphText>
    </StepScreen>
  );
};
